/** Minimum characters before existing-user directory search runs. */
export const INVITE_USER_SEARCH_MIN_CHARS = 5;

/** Debounce for existing-user typeahead. */
export const INVITE_USER_SEARCH_DEBOUNCE_MS = 300;

/** Page size for existing-user search results. */
export const INVITE_USER_SEARCH_PER_PAGE = 20;

export const INVITE_TAB_NEW = "new" as const;
export const INVITE_TAB_EXISTING = "existing" as const;
export type InvitePersonTab = typeof INVITE_TAB_NEW | typeof INVITE_TAB_EXISTING;

export const INVITE_SCOPE_WORD: Record<string, string> = {
    NATION: "nation",
    DISTRICT: "district",
    CHURCH: "church",
};

/** Singular group header for a scope level (e.g. Nation). */
export function inviteScopeGroupLabel(scope: string): string {
    const word = INVITE_SCOPE_WORD[scope] ?? "entity";
    return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Plural group header for child list (e.g. Districts, Churches). */
export function inviteChildGroupLabel(childScope: string): string {
    if (childScope === "CHURCH") return "Churches";
    return `${inviteScopeGroupLabel(childScope)}s`;
}

export const INVITE_DESTINATION_HINT =
    "Select where this person will join.";

export function inviteExistingUserPoolHint(ownScope: string): string {
    switch (ownScope) {
        case "NATION":
            return "Only users in this nation and its districts and churches are listed.";
        case "DISTRICT":
            return "Only users in this district and its churches are listed.";
        case "CHURCH":
            return "Only users in this church are listed.";
        default:
            return "Only users in this workspace are listed.";
    }
}

export function inviteUserSearchMinCharsHint(minChars: number = INVITE_USER_SEARCH_MIN_CHARS): string {
    return `Type at least ${minChars} characters to search.`;
}

export const INVITE_USER_SEARCH_EMPTY =
    "No matching users. Try a different search, or use New person.";

export const INVITE_USER_SEARCH_ERROR =
    "Couldn't load users. Try again.";

export const INVITE_EXISTING_USER_REQUIRED =
    "Select an existing user to invite.";

export interface InviteDestination {
    scope: string;
    scopeRefId: number;
}

/**
 * Encodes destination as SCOPE:id so nation id 3 and district id 3 stay distinct.
 */
export function encodeInviteDestination(scope: string, scopeRefId: number): string {
    return `${scope}:${scopeRefId}`;
}

/**
 * Parses encodeInviteDestination values. Returns null if malformed.
 */
export function parseInviteDestination(value: string | number): InviteDestination | null {
    const raw = String(value);
    const colon = raw.indexOf(":");
    if (colon <= 0) return null;
    const scope = raw.slice(0, colon).toUpperCase();
    const scopeRefId = Number(raw.slice(colon + 1));
    if (!scope || !Number.isFinite(scopeRefId) || scopeRefId <= 0) return null;
    if (!INVITE_SCOPE_WORD[scope]) return null;
    return { scope, scopeRefId };
}
